import React, { useState, useEffect } from 'react';
import { Article, ArticlesResponse } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import BlogCard from './BlogCard';
import './BlogMyArticles.css';

interface BlogMyArticlesProps {
  onEdit: (articleId: number) => void;
  onCreate: () => void;
  onView: (slug: string) => void;
}

const BlogMyArticles: React.FC<BlogMyArticlesProps> = ({ onEdit, onCreate, onView }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'articles' | 'my'>('articles');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const copyArticleLink = (slug: string) => {
    const baseUrl = window.location.origin;
    const articleUrl = `${baseUrl}?article=${slug}`;
    navigator.clipboard.writeText(articleUrl).then(() => {
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2000);
    });
  };

  useEffect(() => {
    if (activeTab === 'articles') {
      fetchPublicArticles();
    } else {
      fetchMyArticles();
    }
  }, [page, activeTab]);

  const fetchPublicArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/articles?page=${page}&per_page=10`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data: ArticlesResponse = await response.json();
        setArticles(data.articles);
        setTotalPages(data.pages);
        setHasNext(data.has_next);
        setHasPrev(data.has_prev);
      } else {
        setError('Failed to load articles');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/blog/my-articles?page=${page}&per_page=10`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data: ArticlesResponse = await response.json();
        setArticles(data.articles);
        setTotalPages(data.pages);
        setHasNext(data.has_next);
        setHasPrev(data.has_prev);
      } else if (response.status === 403) {
        setError('Club membership required to access this feature');
      } else {
        setError('Failed to load articles');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (articleId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/blog/articles/${articleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setArticles(articles.filter(a => a.id !== articleId));
        setDeleteConfirm(null);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete article');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleTabChange = (tab: 'articles' | 'my') => {
    setActiveTab(tab);
    setPage(1);
    setError('');
    setDeleteConfirm(null);
  };

  if (loading) {
    return (
      <div className="my-articles-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="my-articles">
      <div className="my-articles-header">
        <div className="header-left">
          <h2>{t('blog.blog')}</h2>
          <div className="blog-tabs">
            <button
              className={`tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
              onClick={() => handleTabChange('articles')}
            >
              {t('blog.articles')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
              onClick={() => handleTabChange('my')}
            >
              {t('blog.myArticles')}
            </button>
          </div>
        </div>
        <div className="header-right">
          {activeTab === 'my' && (
            <button onClick={onCreate} className="btn btn-create">
              + {t('blog.createArticle')}
            </button>
          )}
        </div>
      </div>

      {error && <div className="my-articles-error">{error}</div>}

      {articles.length === 0 ? (
        <div className="my-articles-empty">
          <p>{t('blog.noArticles')}</p>
          {activeTab === 'my' && <p>Start writing your first article!</p>}
        </div>
      ) : activeTab === 'articles' ? (
        /* Public articles view - read only */
        <>
          <div className="public-articles-grid">
            {articles.map((article) => (
              <div key={article.id} className="article-card-wrapper">
                <BlogCard
                  article={article}
                  onClick={() => onView(article.slug)}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyArticleLink(article.slug);
                  }}
                  className="btn btn-copy-link"
                  title={t('blog.copyLink')}
                >
                  {copiedSlug === article.slug ? '✓ ' + t('blog.copied') : '🔗 ' + t('blog.copyLink')}
                </button>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="my-articles-pagination">
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={!hasPrev}
                className="btn btn-pagination"
              >
                ← {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!hasNext}
                className="btn btn-pagination"
              >
                {t('common.next')} →
              </button>
            </div>
          )}
        </>
      ) : (
        /* Card view for my articles */
        <>
          <div className="my-articles-list">
            {articles.map((article) => (
              <div key={article.id} className="my-article-item">
                <BlogCard
                  article={article}
                  onClick={() => article.status === 'published' ? onView(article.slug) : onEdit(article.id)}
                  showStatus
                />
                <div className="my-article-actions">
                  {article.status === 'published' && (
                    <button
                      onClick={() => copyArticleLink(article.slug)}
                      className="btn btn-copy-link"
                      title={t('blog.copyLink')}
                    >
                      {copiedSlug === article.slug ? '✓' : '🔗'}
                    </button>
                  )}
                  <button
                    onClick={() => onEdit(article.id)}
                    className="btn btn-edit"
                  >
                    {t('common.edit')}
                  </button>
                  {deleteConfirm === article.id ? (
                    <div className="delete-confirm">
                      <span>{t('common.delete')}?</span>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="btn btn-confirm-delete"
                      >
                        {t('common.yes')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="btn btn-cancel-delete"
                      >
                        {t('common.no')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(article.id)}
                      className="btn btn-delete"
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="my-articles-pagination">
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={!hasPrev}
                className="btn btn-pagination"
              >
                ← {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!hasNext}
                className="btn btn-pagination"
              >
                {t('common.next')} →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlogMyArticles;
